// react
import { ChangeEvent, Dispatch, SetStateAction, useContext, useEffect, useState } from 'react';

// types
import {
	Entity,
	EntityPayload,
	Feedback,
	LoadingStatus,
	Response,
	UserPayload,
} from '../../../@types';

// i18n
import { useTranslation } from 'react-i18next';

// context
import { ContextBanner } from '../../../App.context';
import { ContextProject } from '../../pages/Project/Project.context';

// mui
import { Grid } from '@mui/material';

// tables
import { TableBasicUsersOnboard } from '../../tables/basic/TableBasicUsersOnboard/TableBasicUsersOnboard';

// ui
import { AlertFeedback } from '../../ui/AlertFeedback/AlertFeedback';

// hooks
import { useScrollToError } from '../../../hooks/useScrollToError';

// props
export interface FormOnboardUsersProps {
	entity: Entity;
	onClose: () => void;
	onSuccess: () => void;
	setStatus: Dispatch<SetStateAction<LoadingStatus>>;
	usersWbg: UserPayload[];
}

export const FormOnboardUsers = ({
	entity,
	onClose,
	onSuccess,
	setStatus,
	usersWbg,
}: FormOnboardUsersProps) => {
	// session
	const accessToken = sessionStorage.getItem('accessToken');
	const idToken = sessionStorage.getItem('idToken');

	// hooks
	const { t } = useTranslation();

	// context
	const { setBanner } = useContext(ContextBanner);
	const { project } = useContext(ContextProject);

	// state
	const [feedback, setFeedback] = useState<Feedback | null>(null);
	const [selectedIdWbgs, setSelectedIdWbgs] = useState<Set<string>>(
		() => new Set(usersWbg.map((u) => String(u.idWbg)))
	);

	// scroll to feedback alert when it appears after submit
	const errorRef = useScrollToError(feedback ? String(feedback.alert?.children ?? 'error') : null);

	// sync status with selection
	useEffect(() => {
		setStatus((prev) => {
			if (prev === 'LOADING') return prev;
			return selectedIdWbgs.size === 0 ? 'DISABLED' : 'DEFAULT';
		});
	}, [selectedIdWbgs.size, setStatus]);

	const handleChangeAllUsers = (entityPayload: EntityPayload, checked: boolean) => {
		setSelectedIdWbgs(checked ? new Set(usersWbg.map((u) => String(u.idWbg))) : new Set());
	};

	const handleChangeUser = (
		e: ChangeEvent<HTMLInputElement>,
		checked: boolean,
		user: UserPayload
	) => {
		const idWbg = String(user.idWbg || '');
		setSelectedIdWbgs((prev) => {
			const next = new Set(prev);
			if (checked) {
				next.add(idWbg);
			} else {
				next.delete(idWbg);
			}
			return next;
		});
	};

	const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
		e.preventDefault();
		setFeedback(null);
		setStatus('LOADING');

		const selected = usersWbg.filter((u) => selectedIdWbgs.has(String(u.idWbg)));

		const payloads = selected.map((u) => ({
			...u,
			entities: [entity.id],
			projects: [
				{
					id: project?.id,
					status: 'INVITED',
				},
			],
		}));

		try {
			const reqOnboardUsers = await fetch(`${process.env.REACT_APP_API_URL}/users`, {
				method: 'POST',
				headers: {
					Authorization: `Bearer ${accessToken}`,
					'Content-Type': 'application/json',
					User: String(idToken),
				},
				body: JSON.stringify(payloads),
			});

			const resOnboardUsers = await reqOnboardUsers.json();

			if (resOnboardUsers.error) {
				throw new Error(resOnboardUsers.error.message, {
					cause: {
						details: resOnboardUsers.error.details,
						id: resOnboardUsers.error.id,
					},
				});
			}

			const { failed = [], succeeded = [] } = resOnboardUsers.data || {};
			const failedFiltered = failed.filter(
				(res: Response) => res.error?.id !== 'B2B_USER_NOT_PERMITTED'
			);
			const failedB2b = failed.filter(
				(res: Response) => res.error?.id === 'B2B_USER_NOT_PERMITTED'
			);

			if (failedFiltered.length) {
				setFeedback({
					alert: {
						children: t('error.onboardUsersFailed', { count: failedFiltered.length }),
					},
					response: resOnboardUsers,
				});
			} else {
				setBanner({
					message: t('alert.onboardSuccessUsers', { count: failedB2b.length + succeeded.length }),
					severity: 'success',
				});
				onSuccess();
				onClose();
			}
		} catch (error) {
			const message = error instanceof Error ? error.message : 'An unknown error occurred.';
			setFeedback({
				alert: {
					children: message,
				},
			});
		} finally {
			setStatus('DEFAULT');
		}
	};

	return (
		<form id="form-onboard-users" onSubmit={handleSubmit}>
			<Grid container spacing={2}>
				<Grid item xs={12}>
					<TableBasicUsersOnboard
						borderRadius={2}
						color="grey"
						data={usersWbg}
						entity={entity as unknown as EntityPayload}
						hasBorder={true}
						isChecked={true}
						onChangeAllUsers={handleChangeAllUsers}
						onChangeUser={handleChangeUser}
						selectedUserIds={selectedIdWbgs}
					/>
				</Grid>
				{feedback && (
					<Grid item xs={12}>
						<div ref={errorRef}>
							<AlertFeedback feedback={feedback} />
						</div>
					</Grid>
				)}
			</Grid>
		</form>
	);
};
